import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  LocalPost,
  PostTopicType,
  gbpPostsApi,
} from "../../../services/gbp";
import { profilesTrackingService } from "../../../services";
import { Profile } from "../types";
import {
  buildPostDraftSuggestions,
  gabaritoImageNames,
  gabaritoKeywords,
  type PostDraftSuggestion,
} from "../profile-content-suggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import { Badge, Botao, BotaoPequeno, EditorGrid, Hint, Lab } from "./EditorChrome";
import {
  readGabaritoState,
  renameFileWithRandomKeyword,
} from "./gabarito-utils";

// ==================== Helpers ====================


const getTypeIcon = (type: PostTopicType) => {
  switch (type) {
    case "EVENT":
      return "event";
    case "OFFER":
      return "local_offer";
    case "ALERT":
      return "warning";
    default:
      return "article";
  }
};

const getTypeLabel = (type: PostTopicType) => {
  switch (type) {
    case "EVENT":
      return "Evento";
    case "OFFER":
      return "Oferta";
    case "ALERT":
      return "Alerta";
    default:
      return "Publicação";
  }
};

const getCTALabel = (actionType?: string) => {
  switch (actionType) {
    case "BOOK":
      return "Reservar";
    case "ORDER":
      return "Pedir";
    case "SHOP":
      return "Comprar";
    case "LEARN_MORE":
      return "Saiba Mais";
    case "SIGN_UP":
      return "Inscrever-se";
    case "CALL":
      return "Ligar";
    case "GET_OFFER":
      return "Ver Oferta";
    default:
      return "Ver";
  }
};

const getStatusLabel = (status?: string) => {
  switch (status) {
    case "LIVE":
      return "Ativo";
    case "PROCESSING":
      return "Processando";
    case "REJECTED":
      return "Rejeitado";
    default:
      return status || "";
  }
};


// ==================== Animations ====================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ==================== Styled Components (prancheta PerfilPostagens) ====================

const Container = styled.div`
  animation: ${fadeIn} 0.3s ease;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Titulo = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Abas = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid var(--line);
  padding-bottom: 8px;
  flex-wrap: wrap;
`;

const Aba = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => (p.$on ? "#0B4FA8" : "#5F6368")};
  background: ${(p) => (p.$on ? "#EAF1FB" : "transparent")};
  border: 0;
  cursor: pointer;
  &:hover {
    background: ${(p) => (p.$on ? "#EAF1FB" : "#F3F6FA")};
  }
  .material-icons {
    font-size: 18px;
  }
`;

const FiltroTipo = styled.button<{ $on?: boolean }>`
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$on ? "#202124" : "#DADCE0")};
  background: ${(p) => (p.$on ? "#202124" : "#fff")};
  color: ${(p) => (p.$on ? "#fff" : "#3C4043")};
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
`;

const Cartao = styled.section`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 110px 120px 110px;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef1f4;
  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: #f7f9fc;
  }
  .titulo {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: 900px) {
    grid-template-columns: 64px minmax(0, 1fr) 110px;
    .metrica,
    .acoes {
      grid-column: 2 / -1;
    }
  }
  /* Celular: título inteiro; selo, métricas e ações embaixo dele. */
  @media (max-width: 599.95px) {
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 8px 12px;
    & > :nth-child(n + 3) {
      grid-column: 2 / -1;
      justify-self: start;
    }
    .titulo {
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
`;

const Miniatura = styled.div`
  width: 64px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #d6e4f7, #eaf1fb);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0b4fa8;
  overflow: hidden;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const IconeBotao = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #5f6368;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    background: #f3f6fa;
    color: #1f1f1f;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .material-icons {
    font-size: 20px;
  }
`;

const Formulario = styled.section`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 0;
`;

const Tipo = styled.button<{ $on?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$on ? "#1264C7" : "#E2E6EA")};
  background: ${(p) => (p.$on ? "#EAF1FB" : "#fff")};
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: #1f1f1f;
  .material-icons {
    color: ${(p) => (p.$on ? "#0B4FA8" : "#5F6368")};
  }
  &:hover {
    border-color: ${(p) => (p.$on ? "#1264C7" : "#D4D9DE")};
  }
`;

const Entrada = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
`;

const Selecao = styled.select`
  width: 100%;
  height: 44px;
  padding: 0 36px 0 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #1f1f1f;
  background: #fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%235F6368'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  box-sizing: border-box;
`;

const Texto = styled.textarea`
  width: 100%;
  min-height: 112px;
  padding: 12px 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  line-height: 22px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Bloco = styled.div`
  padding: 14px;
  border-radius: 12px;
  background: #f7f9fc;
  display: flex;
  flex-direction: column;
  gap: 12px;
  .titulo {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .titulo .material-icons {
    font-size: 18px;
    color: #0b4fa8;
  }
`;

const AdicionarFoto = styled.label`
  width: 96px;
  height: 72px;
  border-radius: 8px;
  background: #fff;
  border: 1px dashed #c2d7f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1264c7;
  cursor: pointer;
  flex-shrink: 0;
  input {
    display: none;
  }
`;

const Ideia = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #eef1f4;
  &:last-child {
    border-bottom: 0;
  }
  .titulo {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 600;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #5f6368;
  .material-icons {
    font-size: 44px;
    color: #bdc1c6;
    margin-bottom: 12px;
  }
  h4 {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    color: #3c4043;
    margin: 0 0 6px;
  }
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #5f6368;
  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e5e7eb;
    border-top-color: #1264c7;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }
  p {
    margin-top: 14px;
    font-size: 13px;
  }
`;

const Toast = styled.div<{ $type: "error" | "success" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  animation: ${fadeIn} 0.25s ease;
  ${({ $type }) =>
    $type === "error"
      ? css`
          background: #fce8e6;
          color: #b3261e;
        `
      : css`
          background: #eaf7ee;
          color: #137333;
        `}
  .material-icons {
    font-size: 18px;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(31, 35, 41, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 420px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 16px 48px rgba(31, 35, 41, 0.2);
  h4 {
    margin: 0;
    font-family: var(--font-sans);
    font-size: 18px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #b3261e;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: #3c4043;
  }
`;

interface PostsTabProps {
  profile: Profile | null;
  userId?: number;
  /** Cliente com acesso só de leitura: vê as postagens, não cria, edita nem exclui. */
  readOnly?: boolean;
}

export const PostsTab: React.FC<PostsTabProps> = ({ profile, readOnly = false }) => {
  const [posts, setPosts] = useState<LocalPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const publicId = profile?.id;

  // showForm = formulário de NOVA postagem no topo
  // editingPost = formulário de EDIÇÃO inline no card
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<LocalPost | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [aba, setAba] = useState<"publicadas" | "todas" | "ideias">("publicadas");
  const [postDrafts, setPostDrafts] = useState<PostDraftSuggestion[]>([]);
  const [localFormImageName, setLocalFormImageName] = useState("");
  const [localFormImagePreview, setLocalFormImagePreview] = useState("");

  const [formData, setFormData] = useState<LocalPost>({
    summary: "",
    topicType: "STANDARD",
    languageCode: "pt",
  });

  const gabarito = useMemo(
    () => (profile?.id ? readGabaritoState(profile.id) : null),
    [profile?.id],
  );

  const imageOptions = useMemo(
    () => (gabarito ? gabaritoImageNames(gabarito.images) : []),
    [gabarito],
  );

  // ---------- Derivados ----------

  const uniqueTypes = useMemo(() => {
    const set = new Set<PostTopicType>();
    posts.forEach((p) => set.add(p.topicType));
    return Array.from(set);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const base = aba === "publicadas" ? posts.filter((p) => p.state === "LIVE") : posts;
    if (activeFilter === "all") return base;
    return base.filter((p) => p.topicType === activeFilter);
  }, [posts, activeFilter, aba]);

  const liveCount = useMemo(
    () => posts.filter((p) => p.state === "LIVE").length,
    [posts]
  );

  // ---------- Effects ----------

  const fetchPosts = useCallback(async () => {
    if (!publicId) return;

    try {
      setLoading(true);
      setError(null);

      const postsData = await gbpPostsApi.listPosts(publicId);
      
      // Garante que seja sempre um array
      const postsList = Array.isArray(postsData) ? postsData : [];
      setPosts(postsList);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError(
          "Postagens via API estão desabilitadas no servidor. Ative 'google.posts.enabled' ou crie posts no painel do Google."
        );
      } else {
        setError("Não foi possível carregar as postagens. Tente novamente mais tarde.");
      }
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (!profile || !gabarito) {
      setPostDrafts([]);
      return;
    }

    setPostDrafts(
      buildPostDraftSuggestions({
        profile,
        keywords: gabaritoKeywords(gabarito),
        imageNames: gabaritoImageNames(gabarito.images),
      }),
    );
  }, [gabarito, profile]);

  // ---------- Handlers ----------

  const resetForm = () => {
    setFormData({
      summary: "",
      topicType: "STANDARD",
      languageCode: "pt",
    });
    setLocalFormImageName("");
    setLocalFormImagePreview("");
    setEditingPost(null);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (post: LocalPost) => {
    setShowForm(false); // fecha form de nova postagem se estiver aberto
    setEditingPost(post);
    setFormData({ ...post });
  };

  const updatePostDraft = (
    id: string,
    field: "summary" | "imageKeyword",
    value: string,
  ) => {
    setPostDrafts((drafts) =>
      drafts.map((draft) =>
        draft.id === id ? { ...draft, [field]: value } : draft,
      ),
    );
  };

  const readLocalImage = (file: File, onRead: (src: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => onRead(String(reader.result));
    reader.readAsDataURL(file);
  };

  const handleDraftImageUpload = (id: string, file: File | null) => {
    if (!file) return;

    const draft = postDrafts.find((item) => item.id === id);
    const fallbackKeywords = gabarito ? gabaritoKeywords(gabarito) : [];
    const { file: renamedFile, keyword } = renameFileWithRandomKeyword(file, [
      draft?.imageKeyword || "",
      ...imageOptions,
      ...fallbackKeywords,
      profile?.name || "",
    ]);

    readLocalImage(renamedFile, (src) => {
      setPostDrafts((drafts) =>
        drafts.map((draft) =>
          draft.id === id
            ? {
                ...draft,
                imageKeyword: keyword,
                localImageName: renamedFile.name,
                localImagePreview: src,
              }
            : draft,
        ),
      );
    });
  };

  const handleFormImageUpload = (file: File | null) => {
    if (!file) return;

    const { file: renamedFile } = renameFileWithRandomKeyword(file, [
      ...imageOptions,
      ...(gabarito ? gabaritoKeywords(gabarito) : []),
      profile?.name || "",
    ]);

    readLocalImage(renamedFile, (src) => {
      setLocalFormImageName(renamedFile.name);
      setLocalFormImagePreview(src);
      setFormData((prev) => ({
        ...prev,
        media: [
          {
            mediaFormat: "PHOTO",
            sourceUrl: src,
            description: renamedFile.name,
          },
        ],
      }));
    });
  };

  const applyPostDraft = (draft: PostDraftSuggestion) => {
    setEditingPost(null);
    setFormData({
      summary: draft.summary,
      topicType: draft.topicType,
      languageCode: "pt",
      media: draft.localImagePreview
        ? [
            {
              mediaFormat: "PHOTO",
              sourceUrl: draft.localImagePreview,
              description: draft.localImageName || draft.imageKeyword,
            },
          ]
        : undefined,
    });
    setLocalFormImageName(draft.localImageName || "");
    setLocalFormImagePreview(draft.localImagePreview || "");
    setShowForm(true);
  };

  const closeForm = () => {
    console.log("[PostsTab.closeForm] called");
    setShowForm(false);
    resetForm();
  };

  const closeEditForm = () => {
    setEditingPost(null);
    resetForm();
  };

  // Getters removidos — agora usa publicId direto

  // O Google NÃO aceita imagem em base64 (data:) no post — precisa de URL
  // pública. Sobe cada imagem local pro depósito de fotos de post e troca o
  // sourceUrl pela URL retornada. Imagens que já são URL (ex.: Gabarito) passam
  // direto. Falha no upload não bloqueia o post (mantém como estava).
  const ensurePublicPostMedia = async (
    post: LocalPost,
    profileId: string,
  ): Promise<LocalPost> => {
    if (!post.media?.length) return post;
    const media = await Promise.all(
      post.media.map(async (item) => {
        const url = item.sourceUrl;
        if (typeof url === "string" && url.startsWith("data:")) {
          try {
            const uploaded = await profilesTrackingService.addPostImage(
              profileId,
              { fileName: item.description || "post.jpg", src: url },
            );
            if (uploaded?.url) return { ...item, sourceUrl: uploaded.url };
          } catch {
            // mantém como estava; não bloqueia a publicação
          }
        }
        return item;
      }),
    );
    return { ...post, media };
  };

  const handleSave = async (isEdit = false) => {
    if (!publicId || !formData.summary) return;

    try {
      setSaving(true);
      setError(null);

      const payload = await ensurePublicPostMedia(formData, publicId);

      if (isEdit && editingPost?.postId) {
        const updated = await gbpPostsApi.updatePost(
          publicId,
          editingPost.postId,
          payload
        );
        setPosts(posts.map((p) => (p.postId === editingPost.postId ? updated : p)));
        setSuccess("Postagem atualizada!");
        closeEditForm();
      } else {
        const created = await gbpPostsApi.createPost(publicId, payload);
        setPosts([created, ...posts]);
        setSuccess("Postagem criada!");
        closeForm();
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError("Ação não suportada: postagens via API estão desabilitadas no servidor.");
      } else if (emsg.includes("status:502") || /Bad Gateway/i.test(emsg)) {
        // Google retornou 404 ao backend → backend devolveu 502
        const googleDetail = emsg.match(/Google retornou 404[^|]*/i)?.[0];
        setError(
          googleDetail
            ? `Falha na API Google: ${googleDetail.trim()}`
            : "A API do Google para postagens não está disponível. Crie posts diretamente no painel do Google Business Profile."
        );
      } else {
        setError("Erro ao salvar postagem. Tente novamente.");
      }
      setTimeout(() => setError(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!publicId) return;

    try {
      setSaving(true);
      await gbpPostsApi.deletePost(publicId, postId);
      setPosts(posts.filter((p) => p.postId !== postId));
      setDeleteConfirm(null);
      setSuccess("Postagem excluída!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError("Ação não suportada: postagens via API estão desabilitadas no servidor.");
      } else if (emsg.includes("status:502") || /Bad Gateway/i.test(emsg)) {
        setError("A API do Google para postagens não está disponível. Exclua posts diretamente no painel do Google Business Profile.");
      } else {
        setError("Erro ao excluir postagem.");
      }
      setTimeout(() => setError(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Renders ----------

  if (loading) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Carregando postagens...</p>
      </LoadingOverlay>
    );
  }

  if (!publicId) {
    return (
      <Container>
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>
            {error || "Perfil não vinculado ao Google Business Profile."}
          </span>
        </Toast>
      </Container>
    );
  }

  const dataDoPost = (post: LocalPost) => {
    const iso = post.createTime || post.updateTime;
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  };
  const tomDoStatus = (state?: string): "verde" | "azul" | "amarelo" | "cinza" => (state === "LIVE" ? "verde" : state === "PROCESSING" ? "azul" : state === "REJECTED" ? "amarelo" : "cinza");
  const iconeDoStatus = (state?: string) => (state === "LIVE" ? "check_circle" : state === "PROCESSING" ? "schedule" : state === "REJECTED" ? "error" : "draft");
  const formularioAberto = !readOnly && (showForm || Boolean(editingPost));

  return (
    <Container>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Titulo>
            Postagens
            <FieldSyncBadge kind="backend" />
          </Titulo>
          <Hint>{`Novidades, ofertas e eventos publicados na ficha do Google. ${liveCount} ${liveCount === 1 ? "ativa" : "ativas"} de ${posts.length}.`}</Hint>
        </div>
        {!readOnly && (
          <Botao
            type="button"
            $variante={formularioAberto ? "outlined" : "filled"}
            onClick={() => {
              if (editingPost) { closeEditForm(); return; }
              if (showForm) { closeForm(); } else { openAddForm(); }
            }}
          >
            <span className="material-icons">{formularioAberto ? "close" : "add"}</span>
            {formularioAberto ? "Fechar" : "Nova postagem"}
          </Botao>
        )}
      </div>

      {error && (
        <Toast $type="error" role="alert">
          <span className="material-icons">error_outline</span>
          <span>{error}</span>
        </Toast>
      )}
      {success && (
        <Toast $type="success" role="status">
          <span className="material-icons">check_circle</span>
          <span>{success}</span>
        </Toast>
      )}

      <EditorGrid style={{ gridTemplateColumns: formularioAberto ? "minmax(0, 7fr) minmax(0, 5fr)" : "minmax(0, 1fr)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <Abas role="tablist">
            <Aba type="button" role="tab" aria-selected={aba === "publicadas"} $on={aba === "publicadas"} onClick={() => setAba("publicadas")}>
              {`Publicadas · ${liveCount}`}
            </Aba>
            <Aba type="button" role="tab" aria-selected={aba === "todas"} $on={aba === "todas"} onClick={() => setAba("todas")}>
              {`Todas · ${posts.length}`}
            </Aba>
            {!readOnly && (
              <Aba type="button" role="tab" aria-selected={aba === "ideias"} $on={aba === "ideias"} onClick={() => setAba("ideias")}>
                <span className="material-icons">auto_awesome</span>
                {`Ideias da IA · ${postDrafts.length}`}
              </Aba>
            )}
            {aba !== "ideias" && uniqueTypes.length > 1 && (
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <FiltroTipo type="button" $on={activeFilter === "all"} onClick={() => setActiveFilter("all")}>Todos</FiltroTipo>
                {uniqueTypes.map((type) => (
                  <FiltroTipo type="button" key={type} $on={activeFilter === type} onClick={() => setActiveFilter(type)}>
                    {getTypeLabel(type)}
                  </FiltroTipo>
                ))}
              </div>
            )}
          </Abas>

          {aba === "ideias" ? (
            <Cartao>
              {postDrafts.length === 0 && (
                <EmptyState>
                  <span className="material-icons">auto_awesome</span>
                  <h4>Nenhuma ideia ainda</h4>
                  <p>Preencha as palavras e fotos do Gabarito para receber sugestões de postagem.</p>
                </EmptyState>
              )}
              {postDrafts.map((draft) => (
                <Ideia key={draft.id}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <span className="titulo">{draft.title}</span>
                    <Badge $tom="cinza"><FieldSyncBadge kind="local" />Sugestão · valide antes de publicar</Badge>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Foto do Gabarito</Lab>
                      {imageOptions.length > 0 ? (
                        <Selecao value={draft.imageKeyword} onChange={(event) => updatePostDraft(draft.id, "imageKeyword", event.target.value)}>
                          {imageOptions.map((image) => (
                            <option key={image} value={image}>{image}</option>
                          ))}
                        </Selecao>
                      ) : (
                        <Entrada value={draft.imageKeyword} onChange={(event) => updatePostDraft(draft.id, "imageKeyword", event.target.value)} placeholder="Sem foto cadastrada no Gabarito" />
                      )}
                    </Campo>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Foto do computador</Lab>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {draft.localImagePreview && (
                          <Miniatura style={{ width: 96, height: 72 }}>
                            <img src={draft.localImagePreview} alt={draft.localImageName || draft.imageKeyword} />
                          </Miniatura>
                        )}
                        <AdicionarFoto title="Adicionar foto do computador">
                          <span className="material-icons">add_photo_alternate</span>
                          <input
                            type="file"
                            accept="image/*"
                            aria-label={`Foto para ${draft.title}`}
                            onChange={(event) => {
                              handleDraftImageUpload(draft.id, event.target.files?.[0] || null);
                              event.target.value = "";
                            }}
                          />
                        </AdicionarFoto>
                      </div>
                      {draft.localImageName && <Hint>{`Foto selecionada: ${draft.localImageName}. Clique em "Usar como nova postagem" para aplicar.`}</Hint>}
                    </Campo>
                  </div>
                  <Campo>
                    <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Texto da postagem</Lab>
                    <Texto value={draft.summary} onChange={(event) => updatePostDraft(draft.id, "summary", event.target.value)} />
                  </Campo>
                  <BotaoPequeno type="button" $variante="filled" style={{ alignSelf: "flex-start" }} onClick={() => { applyPostDraft(draft); }}>
                    <span className="material-icons">edit</span>
                    Usar como nova postagem
                  </BotaoPequeno>
                </Ideia>
              ))}
            </Cartao>
          ) : (
            <Cartao>
              {filteredPosts.length === 0 ? (
                <EmptyState>
                  <span className="material-icons">article</span>
                  <h4>Nenhuma postagem encontrada</h4>
                  <p>{posts.length === 0 ? "Crie postagens para engajar seus clientes no Google." : "Nenhuma postagem com esse filtro."}</p>
                </EmptyState>
              ) : (
                filteredPosts.map((post, index) => (
                  <Item key={post.postId || post.name || `post-${index}`}>
                    <Miniatura>
                      {post.media?.[0]?.sourceUrl ? <img src={post.media[0].sourceUrl} alt="" /> : <span className="material-icons">{getTypeIcon(post.topicType)}</span>}
                    </Miniatura>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <span className="titulo" title={post.summary}>{post.event?.title || post.summary}</span>
                      <Hint>
                        {[getTypeLabel(post.topicType), dataDoPost(post), post.offer?.couponCode ? `cupom ${post.offer.couponCode}` : null].filter(Boolean).join(" · ")}
                      </Hint>
                    </div>
                    <Badge $tom={tomDoStatus(post.state)}>
                      <span className="material-icons">{iconeDoStatus(post.state)}</span>
                      {getStatusLabel(post.state) || "Rascunho"}
                    </Badge>
                    <Hint className="metrica">
                      {post.metrics?.viewsCount !== undefined
                        ? `${post.metrics.viewsCount} visualizações${post.metrics?.clicksCount !== undefined ? ` · ${post.metrics.clicksCount} cliques` : ""}`
                        : "—"}
                    </Hint>
                    <div className="acoes" style={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                      {post.callToAction?.url && (
                        <IconeBotao as="a" href={post.callToAction.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir link: ${getCTALabel(post.callToAction.actionType)}`} title={getCTALabel(post.callToAction.actionType)}>
                          <span className="material-icons">open_in_new</span>
                        </IconeBotao>
                      )}
                      {!readOnly && (
                        <>
                          <IconeBotao type="button" aria-label="Editar postagem" title="Editar" onClick={() => { openEditForm(post); }}>
                            <span className="material-icons">edit</span>
                          </IconeBotao>
                          <IconeBotao type="button" aria-label="Excluir postagem" title="Excluir" onClick={() => setDeleteConfirm(post.postId || "")}>
                            <span className="material-icons">delete</span>
                          </IconeBotao>
                        </>
                      )}
                    </div>
                  </Item>
                ))
              )}
            </Cartao>
          )}
        </div>

        {formularioAberto && (
          <Formulario aria-label={editingPost ? "Editar postagem" : "Nova postagem"}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600 }}>{editingPost ? "Editar postagem" : "Nova postagem"}</span>
              {editingPost?.state && <Badge $tom={tomDoStatus(editingPost.state)}>{getStatusLabel(editingPost.state)}</Badge>}
            </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {(["STANDARD", "EVENT", "OFFER"] as PostTopicType[]).map((type) => (
                  <Tipo key={type} type="button" $on={formData.topicType === type} aria-pressed={formData.topicType === type} onClick={() => setFormData({ ...formData, topicType: type })}>
                    <span className="material-icons">{getTypeIcon(type)}</span>
                    {getTypeLabel(type)}
                  </Tipo>
                ))}
              </div>

              <Campo>
                <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Foto<FieldSyncBadge kind="backend" /></Lab>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {(localFormImagePreview || formData.media?.[0]?.sourceUrl) && (
                    <Miniatura style={{ width: 96, height: 72 }}>
                      <img src={localFormImagePreview || formData.media?.[0]?.sourceUrl} alt={localFormImageName || "Foto da postagem"} />
                    </Miniatura>
                  )}
                  <AdicionarFoto title="Adicionar foto do computador">
                    <span className="material-icons">add_photo_alternate</span>
                    <input
                      type="file"
                      accept="image/*"
                      aria-label="Adicionar foto do computador"
                      onChange={(event) => {
                        handleFormImageUpload(event.target.files?.[0] || null);
                        event.target.value = "";
                      }}
                    />
                  </AdicionarFoto>
                  <Hint style={{ alignSelf: "center" }}>
                    {localFormImageName ? `Foto selecionada: ${localFormImageName}` : imageOptions.length > 0 ? `Sugestões do Gabarito: ${imageOptions.length} ${imageOptions.length === 1 ? "foto" : "fotos"}` : "PNG ou JPG. Opcional."}
                  </Hint>
                </div>
                <Entrada
                  type="url"
                  aria-label="URL da imagem"
                  value={formData.media?.[0]?.sourceUrl || ""}
                  onChange={(e) => setFormData({ ...formData, media: e.target.value ? [{ mediaFormat: "PHOTO", sourceUrl: e.target.value }] : undefined })}
                  placeholder="ou cole a URL da imagem"
                />
              </Campo>

              <Campo>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Texto *<FieldSyncBadge kind="backend" /></Lab>
                  <Hint>{`${(formData.summary || "").length.toLocaleString("pt-BR")} / 1.500`}</Hint>
                </div>
                <Texto
                  value={formData.summary}
                  maxLength={1500}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Escreva o conteúdo da sua postagem..."
                />
              </Campo>

              {formData.topicType === "EVENT" && (
                <Bloco>
                  <span className="titulo"><span className="material-icons">event</span>Detalhes do evento</span>
                  <Campo>
                    <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Título do evento</Lab>
                    <Entrada type="text" value={formData.event?.title || ""} onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, title: e.target.value } })} placeholder="Nome do evento" />
                  </Campo>
                  <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Início</Lab>
                      <Entrada type="datetime-local" value={formData.event?.startTime?.slice(0, 16) || ""} onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, startTime: e.target.value } })} />
                    </Campo>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Fim</Lab>
                      <Entrada type="datetime-local" value={formData.event?.endTime?.slice(0, 16) || ""} onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, endTime: e.target.value } })} />
                    </Campo>
                  </div>
                </Bloco>
              )}

              {formData.topicType === "OFFER" && (
                <Bloco>
                  <span className="titulo"><span className="material-icons">local_offer</span>Detalhes da oferta</span>
                  <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Código do cupom</Lab>
                      <Entrada type="text" value={formData.offer?.couponCode || ""} onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, couponCode: e.target.value } })} placeholder="Ex: SAVE20" />
                    </Campo>
                    <Campo>
                      <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>URL de resgate</Lab>
                      <Entrada type="url" value={formData.offer?.redeemOnlineUrl || ""} onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, redeemOnlineUrl: e.target.value } })} placeholder="https://..." />
                    </Campo>
                  </div>
                  <Campo>
                    <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Termos e condições</Lab>
                    <Entrada type="text" value={formData.offer?.termsConditions || ""} onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, termsConditions: e.target.value } })} placeholder="Ex: Válido até 31/12/2026" />
                  </Campo>
                </Bloco>
              )}

              <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Campo>
                  <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>Botão</Lab>
                  <Selecao aria-label="Tipo de ação" value={formData.callToAction?.actionType || ""} onChange={(e) => setFormData({ ...formData, callToAction: { ...formData.callToAction!, actionType: e.target.value } })}>
                    <option value="">Sem botão</option>
                    <option value="LEARN_MORE">Saiba mais</option>
                    <option value="BOOK">Reservar</option>
                    <option value="ORDER">Pedir</option>
                    <option value="SHOP">Comprar</option>
                    <option value="SIGN_UP">Inscrever-se</option>
                    <option value="CALL">Ligar</option>
                    <option value="GET_OFFER">Ver oferta</option>
                  </Selecao>
                </Campo>
                <Campo>
                  <Lab style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}>URL do botão</Lab>
                  <Entrada type="url" aria-label="URL do botão" value={formData.callToAction?.url || ""} onChange={(e) => setFormData({ ...formData, callToAction: { ...formData.callToAction!, url: e.target.value } })} placeholder="https://..." />
                </Campo>
              </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
              <BotaoPequeno type="button" $variante="text" onClick={editingPost ? closeEditForm : closeForm}>
                Cancelar
              </BotaoPequeno>
              <Botao type="button" $variante="filled" onClick={() => handleSave(Boolean(editingPost))} disabled={saving || !formData.summary}>
                <span className="material-icons">{editingPost ? "save" : "send"}</span>
                {saving ? "Salvando..." : editingPost ? "Atualizar" : "Publicar agora"}
              </Botao>
            </div>
          </Formulario>
        )}
      </EditorGrid>

      {deleteConfirm && (
        <ModalBackdrop onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <ModalBox role="dialog" aria-label="Confirmar exclusão">
            <h4>
              <span className="material-icons">warning</span>
              Excluir postagem
            </h4>
            <p>Tem certeza que deseja excluir esta postagem? Esta ação não pode ser desfeita.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
              <BotaoPequeno type="button" $variante="text" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </BotaoPequeno>
              <BotaoPequeno type="button" $variante="outlined" style={{ color: "#B3261E", borderColor: "#F9DEDC" }} onClick={() => handleDelete(deleteConfirm)} disabled={saving}>
                <span className="material-icons">delete_forever</span>
                {saving ? "Excluindo..." : "Excluir"}
              </BotaoPequeno>
            </div>
          </ModalBox>
        </ModalBackdrop>
      )}
    </Container>
  );
};

export default PostsTab;
